import { AuthService } from '../../../services/auth/auth.service';
import { inject, Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { PermissionService } from '../../../services/permission.service';
import { Permission } from '../../../models/access.model';

interface PermissionGroup {
  category: string;
  permissions: Permission[];
}

type StatusFilter = 'all' | 'active' | 'inactive';

@Component({
  selector: 'app-permissions-page',
  standalone: false,
  templateUrl: './permissions-page.component.html',
  styleUrls: ['./permissions-page.component.css']
})
export class PermissionsPageComponent implements OnInit {
  readonly staffAuth = inject(AuthService);
  constructor(private fb: FormBuilder, private permissionService: PermissionService) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['']
    });
  }

  message = '';

  form!: FormGroup;

  permissions: Permission[] = [];
  groups: PermissionGroup[] = [];
  categories: string[] = [];

  searchTerm = '';
  categoryFilter = 'all';
  statusFilter: StatusFilter = 'all';

  isLoading = false;
  loadError = '';
  showAddForm = false;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.loadError = '';

    this.permissionService.getPermissions().subscribe({
      next: perms => {
        this.permissions = perms ?? [];
        this.categories = Array.from(
          new Set(this.permissions.map(p => this.categoryOf(p)))
        ).sort((a, b) => a.localeCompare(b));
        this.applyFilters();
      },
      error: err => {
        console.error('Failed to load permissions', err);
        this.loadError = 'Could not load permissions. Please try again.';
        this.permissions = [];
        this.categories = [];
        this.groups = [];
      }
    }).add(() => (this.isLoading = false));
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();

    const matches = this.permissions.filter(p => {
      if (this.categoryFilter !== 'all' && this.categoryOf(p) !== this.categoryFilter) {
        return false;
      }
      if (this.statusFilter === 'active' && p.isActive === false) return false;
      if (this.statusFilter === 'inactive' && p.isActive !== false) return false;
      if (!term) return true;

      return (
        (p.code || '').toLowerCase().includes(term) ||
        (p.name || '').toLowerCase().includes(term) ||
        (p.description || '').toLowerCase().includes(term) ||
        this.categoryOf(p).toLowerCase().includes(term)
      );
    });

    const buckets: { [category: string]: Permission[] } = {};
    for (const perm of matches) {
      const key = this.categoryOf(perm);
      (buckets[key] ||= []).push(perm);
    }

    this.groups = Object.keys(buckets)
      .sort((a, b) => a.localeCompare(b))
      .map(category => ({
        category,
        permissions: buckets[category].sort((a, b) =>
          (a.code || a.name || '').localeCompare(b.code || b.name || '')
        )
      }));
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.categoryFilter = 'all';
    this.statusFilter = 'all';
    this.applyFilters();
  }

  get matchCount(): number {
    return this.groups.reduce((total, g) => total + g.permissions.length, 0);
  }

  get hasFilters(): boolean {
    return !!this.searchTerm.trim() || this.categoryFilter !== 'all' || this.statusFilter !== 'all';
  }

  private categoryOf(permission: Permission): string {
    return (permission.category || '').trim() || 'Uncategorized';
  }

  toggleAddForm(): void {
    if (!this.staffAuth.can('Platform.Roles.ManagePermissions')) return;
    this.showAddForm = !this.showAddForm;
    this.message = '';
  }

  submit() {
    if (!this.staffAuth.can('Platform.Roles.ManagePermissions')) return;
    if (this.form.invalid) {
      this.message = 'Please enter a valid permission name.';
      return;
    }
    const payload = {
      name: this.form.value.name!,
      description: this.form.value.description || ''
    };
    this.permissionService.createPermission(payload).subscribe({
      next: () => {
        this.message = 'Permission created successfully';
        this.form.reset();
        this.load();
      },
      error: (err: any) => {
        this.message = 'Error creating permission';
        console.error(err);
      }
    });
  }
}
